<div class="api-docs">

  <header class="docs-header">
    <div class="docs-header-inner">
      <div class="docs-brand">
        <span class="material-symbols-outlined">api</span>
        <div>
          <div class="docs-eyebrow">ACE Order Gateway</div>
          <h1>External API</h1>
        </div>
      </div>
      <button type="button" class="base-url" (click)="copy('base-url', baseUrl)" title="Copy base URL">
        <span class="base-url-label">Base URL</span>
        <code>{{ baseUrl }}</code>
        <span class="material-symbols-outlined">{{ copiedKey == 'base-url' ? 'check' : 'content_copy' }}</span>
      </button>
    </div>
  </header>

  <div class="docs-layout">

    <!-- NAVIGATION -->
    <nav class="docs-nav" aria-label="API documentation">
      <div class="nav-group" *ngFor="let group of navigation">
        <div class="nav-group-title">{{ group.title }}</div>
        <a *ngFor="let item of group.items" class="nav-item" [class.active]="activeSectionId == item.id"
          [href]="'#' + item.id" (click)="scrollTo(item.id, $event)">
          <span *ngIf="item.method" class="method method-small" [ngClass]="'method-' + item.method.toLowerCase()">{{ item.method }}</span>
          <span>{{ item.label }}</span>
        </a>
      </div>
    </nav>

    <main class="docs-content">

      <!-- INTRODUCTION -->
      <section id="introduction" data-doc-section class="doc-section">
        <h2>Introduction</h2>
        <p>
          The external API lets your system list the products you can order, create orders from your own print
          files or images, and get callbacks when an order is printed, dispatched, delivered or canceled.
        </p>
        <ul class="facts">
          <li><span class="material-symbols-outlined">link</span><span>Every route is relative to the base URL <code>{{ baseUrl }}</code>.</span></li>
          <li><span class="material-symbols-outlined">storefront</span><span>Replace <code>{{ '{' }}storeId{{ '}' }}</code> with the ID of your store. You get it, and the access code, from ACE.</span></li>
          <li><span class="material-symbols-outlined">data_object</span><span>Request and response bodies are JSON. Send <code>Content-Type: application/json</code>.</span></li>
        </ul>
      </section>

      <!-- AUTHENTICATION -->
      <section id="authentication" data-doc-section class="doc-section">
        <h2>Authentication</h2>
        <p>
          Authenticate every request with an <code>Access-Code</code> header holding your store's access code.
        </p>
        <ng-container *ngTemplateOutlet="codeBlock; context: { label: 'Header', key: 'auth-header', code: 'Access-Code: ACCESS_CODE' }"></ng-container>
        <div class="callout">
          <span class="material-symbols-outlined">shield_lock</span>
          <div>Keep the access code on your server. Never call the API from a browser or an app, where it can be read.</div>
        </div>
      </section>

      <!-- ERRORS -->
      <section id="errors" data-doc-section class="doc-section">
        <h2>Errors</h2>
        <p>
          Successful requests respond with status 200 and a JSON body. Check the status code first: some errors
          respond with an empty body.
        </p>
        <div class="table-wrapper">
          <table class="doc-table">
            <thead>
              <tr><th>Status</th><th>Body</th><th>When</th></tr>
            </thead>
            <tbody>
              <tr><td><span class="status status-4xx">401</span></td><td>Empty</td><td>The <code>Access-Code</code> header is missing or wrong.</td></tr>
              <tr><td><span class="status status-ok">200</span></td><td>Empty</td><td>There is no store with the store ID.</td></tr>
              <tr><td><span class="status status-5xx">500</span></td><td><code>{{ '{' }} "error": "…" {{ '}' }}</code></td><td>Something went wrong on our side. Retry later.</td></tr>
            </tbody>
          </table>
        </div>
        <ng-container *ngTemplateOutlet="codeBlock; context: { label: 'Error response', key: 'error-shape', code: json(errorShapeExample) }"></ng-container>
      </section>

      <!-- ENDPOINTS -->
      <section *ngFor="let endpoint of endpoints" [id]="endpoint.id" data-doc-section class="doc-section endpoint">
        <h2>{{ endpoint.title }}</h2>
        <div class="endpoint-route">
          <span class="method" [ngClass]="'method-' + endpoint.method.toLowerCase()">{{ endpoint.method }}</span>
          <code>{{ endpoint.path }}</code>
        </div>
        <p *ngFor="let paragraph of endpoint.description">{{ paragraph }}</p>

        <div class="endpoint-grid">
          <div class="endpoint-spec">
            <ng-container *ngIf="endpoint.queryParams?.length">
              <h3>Query parameters</h3>
              <ng-container *ngTemplateOutlet="fieldTable; context: { fields: endpoint.queryParams }"></ng-container>
            </ng-container>

            <ng-container *ngIf="endpoint.requestFields?.length">
              <h3>Request body</h3>
              <ng-container *ngTemplateOutlet="fieldTable; context: { fields: endpoint.requestFields }"></ng-container>
            </ng-container>

            <h3>Response</h3>
            <ng-container *ngTemplateOutlet="fieldTable; context: { fields: endpoint.responseFields }"></ng-container>

            <h3>Errors</h3>
            <div class="table-wrapper">
              <table class="doc-table">
                <thead>
                  <tr><th>Status</th><th>Body</th><th>When</th></tr>
                </thead>
                <tbody>
                  <tr *ngFor="let error of endpoint.errors">
                    <td><span class="status" [class.status-ok]="error.status < 400" [class.status-4xx]="error.status >= 400 && error.status < 500" [class.status-5xx]="error.status >= 500">{{ error.status }}</span></td>
                    <td><code class="muted-code">{{ error.response }}</code></td>
                    <td>{{ error.description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="endpoint-examples">
            <ng-container *ngTemplateOutlet="codeBlock; context: { label: 'Request', key: endpoint.id + '-curl', code: curl(endpoint) }"></ng-container>
            <ng-container *ngIf="endpoint.requestExample">
              <ng-container *ngTemplateOutlet="codeBlock; context: { label: 'Request body', key: endpoint.id + '-request', code: json(endpoint.requestExample) }"></ng-container>
            </ng-container>
            <ng-container *ngTemplateOutlet="codeBlock; context: { label: 'Response 200', key: endpoint.id + '-response', code: json(endpoint.responseExample) }"></ng-container>
          </div>
        </div>
      </section>

      <!-- PRINT FILES -->
      <section id="print-files" data-doc-section class="doc-section">
        <h2>Print files</h2>
        <p>
          Every poster and canvas item needs a print file before the order can be printed. Give each item one of these:
        </p>
        <div class="table-wrapper">
          <table class="doc-table">
            <thead>
              <tr><th>Field</th><th>Use when</th><th>What happens</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><code>printFileUrl</code></td>
                <td>You have a print-ready PDF file in the exact size of the SKU. The path must end with <code>.pdf</code>.</td>
                <td>The file is printed as it is. The item is <code>READY</code> immediately.</td>
              </tr>
              <tr>
                <td><code>originalFileUrl</code></td>
                <td>You have an image and want us to make the print file.</td>
                <td>
                  A product is created from the image, or reused if the store already has one with the same URL. The
                  item is <code>PENDING_POSTER</code> until its print file has been generated in the background, and
                  then <code>READY</code>.
                </td>
              </tr>
              <tr>
                <td>Neither</td>
                <td>The print file is added by hand in the gateway.</td>
                <td>The item is <code>AWAITING_PRINT_FILE</code> until a print file is added.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3>Requirements for originalFileUrl</h3>
        <ul class="facts">
          <li><span class="material-symbols-outlined">lock</span><span>A direct <code>https</code> link to the file. Redirects are not followed.</span></li>
          <li><span class="material-symbols-outlined">image</span><span>The path ends with <code>.jpg</code>, <code>.jpeg</code>, <code>.png</code>, <code>.webp</code>, <code>.tif</code> or <code>.tiff</code>. A query string after it is fine, as long as it contains no <code>.</code>.</span></li>
          <li><span class="material-symbols-outlined">crop</span><span>The image is scaled to cover the whole print area and cropped from the center. If its aspect ratio differs from the size of the SKU, the edges on the long side are cut off. Nothing is stretched and no white borders are added.</span></li>
          <li><span class="material-symbols-outlined">high_quality</span><span>Use an image with enough resolution to print at the size. It is not upscaled.</span></li>
          <li><span class="material-symbols-outlined">schedule</span><span>The URL must stay accessible until the order has been printed.</span></li>
        </ul>
        <div class="callout">
          <span class="material-symbols-outlined">info</span>
          <div>Use the same <code>originalFileUrl</code> for the same image in later orders. The product and its print files are then reused.</div>
        </div>
      </section>

      <!-- CALLBACKS -->
      <section id="callbacks" data-doc-section class="doc-section">
        <h2>Callbacks</h2>
        <p>
          When an order is created with <code>callbackUrl</code>, that URL receives a <code>POST</code> with a JSON body
          when the order reaches one of these milestones.
        </p>
        <div class="table-wrapper">
          <table class="doc-table">
            <thead>
              <tr><th>Name</th><th>When</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let callback of callbackNames">
                <td><code>{{ callback.name }}</code></td>
                <td>{{ callback.description }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul class="facts">
          <li><span class="material-symbols-outlined">timer</span><span>Respond with a 2xx status. Failed callbacks are not retried, so use <a class="type-link" href="#get-order" (click)="scrollTo('get-order', $event)">Get an order</a> to check an order whose callback you may have missed.</span></li>
          <li><span class="material-symbols-outlined">history</span><span>Every callback and its response is saved in the order's log in the gateway.</span></li>
        </ul>
        <div class="endpoint-grid">
          <div class="endpoint-spec">
            <h3>Body</h3>
            <ng-container *ngTemplateOutlet="fieldTable; context: { fields: callbackFields }"></ng-container>
          </div>
          <div class="endpoint-examples">
            <ng-container *ngTemplateOutlet="codeBlock; context: { label: 'Callback body', key: 'callback', code: json(callbackExample) }"></ng-container>
          </div>
        </div>
      </section>

      <!-- SANDBOX -->
      <section id="sandbox" data-doc-section class="doc-section">
        <h2>Sandbox orders</h2>
        <p>
          Set <code>sandbox</code> to <code>true</code> to test your integration. A sandbox order goes through the
          same steps as a real order, but it is sent to a test folder instead of the print partner and is marked
          as printed automatically, without an <code>ORDER_PRINTED</code> callback. Nothing is printed or shipped.
        </p>
      </section>

      <!-- ORDER STATUSES -->
      <section id="order-statuses" data-doc-section class="doc-section">
        <h2>Order statuses</h2>
        <div class="status-flow">
          <span>PENDING</span><span class="material-symbols-outlined">arrow_forward</span>
          <span>READY</span><span class="material-symbols-outlined">arrow_forward</span>
          <span>QUEUED_FOR_PRINT</span><span class="material-symbols-outlined">arrow_forward</span>
          <span>PRINTED</span><span class="material-symbols-outlined">arrow_forward</span>
          <span>SHIPPED</span><span class="material-symbols-outlined">arrow_forward</span>
          <span>DELIVERED</span>
        </div>
        <div class="table-wrapper">
          <table class="doc-table">
            <thead>
              <tr><th>Status</th><th>Meaning</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let status of orderStatuses">
                <td><code>{{ status.status }}</code></td>
                <td>{{ status.description }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- DATA TYPES -->
      <section *ngFor="let type of types" [id]="type.id" data-doc-section class="doc-section">
        <h2 class="type-title"><span class="type-badge">type</span>{{ type.name }}</h2>
        <p>{{ type.description }}</p>
        <ng-container *ngTemplateOutlet="fieldTable; context: { fields: type.fields }"></ng-container>
      </section>

    </main>
  </div>
</div>


<!-- FIELD TABLE -->
<ng-template #fieldTable let-fields="fields">
  <div class="table-wrapper">
    <table class="doc-table field-table">
      <thead>
        <tr><th>Field</th><th>Type</th><th>Description</th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let field of fields">
          <td>
            <code class="field-name">{{ field.name }}</code>
            <span *ngIf="field.required" class="required">required</span>
          </td>
          <td>
            <a *ngIf="field.typeRef; else plainType" class="type-link" [href]="'#' + field.typeRef" (click)="scrollTo(field.typeRef, $event)">{{ field.type }}</a>
            <ng-template #plainType><span class="type">{{ field.type }}</span></ng-template>
          </td>
          <td>{{ field.description }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</ng-template>


<!-- CODE BLOCK -->
<ng-template #codeBlock let-label="label" let-code="code" let-key="key">
  <div class="code-block">
    <div class="code-block-header">
      <span>{{ label }}</span>
      <button type="button" class="copy-button" (click)="copy(key, code)">
        <span class="material-symbols-outlined">{{ copiedKey == key ? 'check' : 'content_copy' }}</span>
        {{ copiedKey == key ? 'Copied' : 'Copy' }}
      </button>
    </div>
    <pre><code>{{ code }}</code></pre>
  </div>
</ng-template>
